import api, { getErrorMessage } from './api';

const adminService = {
  // ==================== Approvals ====================

  /**
   * Get pending points transfer approval requests (admin/team_admin only)
   * @returns {Promise<Array>} - Pending transfer requests
   */
  getPendingApprovals: async () => {
    try {
      const response = await api.get('/api/v1/approvals/points/');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Approve points transfer request (admin/team_admin only)
   * @param {string} requestId - Transfer request ID
   * @returns {Promise<object>} - Approval result
   */
  approveTransfer: async (requestId) => {
    try {
      const response = await api.post(`/api/v1/approvals/points/${requestId}/approve`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Reject points transfer request (admin/team_admin only)
   * @param {string} requestId - Transfer request ID
   * @param {string} reason - Rejection reason
   * @returns {Promise<object>} - Rejection result
   */
  rejectTransfer: async (requestId, reason) => {
    try {
      const response = await api.post(`/api/v1/approvals/points/${requestId}/reject`, {
        memo: reason,
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  // ==================== Organizations ====================

  /**
   * List all organizations
   */
  listOrganizations: async (params = {}) => {
    try {
      const response = await api.get('/api/v1/admin/organizations/', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Create a new organization
   */
  createOrganization: async (payload) => {
    try {
      const response = await api.post('/api/v1/admin/organizations/', payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Update an organization
   */
  updateOrganization: async (organizationId, payload) => {
    try {
      const response = await api.put(`/api/v1/admin/organizations/${organizationId}`, payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Delete an organization
   */
  deleteOrganization: async (organizationId) => {
    try {
      const response = await api.delete(`/api/v1/admin/organizations/${organizationId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Create Workvivo organization
   */
  createWorkvivoOrganization: async (payload) => {
    try {
      const response = await api.post('/api/v1/workvivo/setup', payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get Workvivo setup status
   */
  getWorkvivoSetupStatus: async () => {
    try {
      const response = await api.get('/api/v1/workvivo/setup-status');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  // ==================== Budgets ====================

  /**
   * List all budgets
   */
  listBudgets: async () => {
    try {
      const response = await api.get('/api/v1/admin/budgets/');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Create a budget
   */
  createBudget: async (payload) => {
    try {
      const response = await api.post('/api/v1/admin/budgets/', payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Update a budget
   */
  updateBudget: async (budgetId, payload) => {
    try {
      const response = await api.put(`/api/v1/admin/budgets/${budgetId}`, payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Delete a budget
   */
  deleteBudget: async (budgetId) => {
    try {
      const response = await api.delete(`/api/v1/admin/budgets/${budgetId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Allocate budget to a team
   */
  allocateTeamBudget: async (budgetId, payload) => {
    try {
      const response = await api.post(`/api/v1/admin/budgets/${budgetId}/allocate`, payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get budget usage metrics
   */
  getBudgetUsage: async (budgetId) => {
    try {
      const response = await api.get(`/api/v1/admin/budgets/${budgetId}/usage`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * List budget spend transactions
   */
  listBudgetSpend: async (budgetId, params = {}) => {
    try {
      const response = await api.get(`/api/v1/admin/budgets/${budgetId}/spend`, { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * List budget reservations
   */
  listBudgetReservations: async (budgetId, params = {}) => {
    try {
      const response = await api.get(`/api/v1/admin/budgets/${budgetId}/reservations`, { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get provisioned budget summary
   */
  getProvisionedBudget: async () => {
    try {
      const response = await api.get('/api/v1/admin/budgets/provision');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  // ==================== Nominations & Campaigns ====================

  /**
   * List all nominations
   */
  listNominations: async () => {
    try {
      const response = await api.get('/api/v1/admin/nominations/');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * List all campaigns
   */
  listCampaigns: async () => {
    try {
      const response = await api.get('/api/v1/admin/nominations/campaigns');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Create a campaign
   */
  createCampaign: async (payload) => {
    try {
      const response = await api.post('/api/v1/admin/nominations/campaigns', payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get campaign details with nominations
   */
  getCampaignDetails: async (campaignId) => {
    try {
      const response = await api.get(`/api/v1/admin/nominations/campaigns/${campaignId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get campaign statistics
   */
  getCampaignStats: async (campaignId) => {
    try {
      const response = await api.get(`/api/v1/admin/nominations/campaigns/${campaignId}/stats`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  // ==================== Recognitions ====================

  /**
   * List all recognitions (admin view)
   */
  listRecognitions: async () => {
    try {
      const response = await api.get('/api/v1/admin/recognitions/');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  // ==================== Awards ====================
  // Note: Awards endpoints are not available in the current backend.
  // These methods return empty results to prevent frontend errors.

  /**
   * List all awards (not available in backend)
   */
  listAwards: async () => {
    console.warn('Awards API not available in backend');
    return [];
  },

  /**
   * Create an award (not available in backend)
   */
  createAward: async (payload) => {
    console.warn('Awards API not available in backend');
    throw new Error('Awards feature is not available');
  },

  /**
   * Update an award (not available in backend)
   */
  updateAward: async (awardId, payload) => {
    console.warn('Awards API not available in backend');
    throw new Error('Awards feature is not available');
  },

  /**
   * Delete an award (not available in backend)
   */
  deleteAward: async (awardId) => {
    console.warn('Awards API not available in backend');
    throw new Error('Awards feature is not available');
  },

  /**
   * Link award to campaign (not available in backend)
   */
  linkAwardToCampaign: async (payload) => {
    console.warn('Awards API not available in backend');
    throw new Error('Awards feature is not available');
  },

  /**
   * List campaign awards (not available in backend)
   */
  listCampaignAwards: async (campaignId) => {
    console.warn('Awards API not available in backend');
    return [];
  },

  // ==================== Integrations ====================

  /**
   * Get rewards platform status
   */
  getRewardsStatus: async () => {
    try {
      const response = await api.get('/api/v1/rewards/status');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Sync rewards catalog
   */
  syncRewardsCatalog: async () => {
    try {
      const response = await api.post('/api/v1/rewards/sync-catalog');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  // ==================== Wallet / Points ====================

  /**
   * Admin adjust user points
   */
  adjustUserPoints: async (userId, payload) => {
    try {
      // Backend expects query params: amount and reason
      const response = await api.post(`/api/v1/wallet/admin/adjust/${userId}`, null, {
        params: {
          amount: payload.amount,
          reason: payload.reason,
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },
};

export default adminService;
